// This is a COPY of downread/shared/src/userPlans.ts. To modify, edit the original and then run downread/bin/build-shared.sh
export const userPlans = ['free', 'pro', 'vip', 'earlyAdopter', 'test'] as const;

export type UserPlan = typeof userPlans[number];

// Plans an admin can assign but that are never offered to end users. 'test' exists
// only to exercise quota enforcement, so it is hidden from the normal plan UI and
// can only be set through admin tooling.
export const adminOnlyUserPlans: readonly UserPlan[] = ['test'];

// Human-readable names for the UI (admin dropdowns, account page).
export const planDisplayNames: Record<UserPlan, string> = {
    free: 'Free',
    pro: 'Pro',
    vip: 'VIP',
    earlyAdopter: 'Early Adopter',
    test: 'Test',
};

export function isUserPlan(value: unknown): value is UserPlan {
    return typeof value == 'string' && (userPlans as readonly string[]).includes(value);
}

export interface PlanLimits {
    maxFeeds: number;
    maxPrivateFeeds: number;
    maxNewsletterFeeds: number;
    // Hard cap on the number of (non-private) feeds a user may create. Tracked via User.createdFeeds.
    // A safety/abuse limit, the same for every real plan; 'test' uses a tiny value so it's testable.
    maxCreatedFeeds: number;
    // Per-feed keyword filters that auto-mark matching posts as read. Evaluated purely client-side.
    keywordFilters: boolean;
    // Retention/size limits for newsletter inboxes (mailboxes) and private feeds.
    // Age limits are in days; undefined = no age limit. Storage is the overall cap in MB
    // across all inboxes resp. all private feeds of the user. The per-feed message caps are
    // enforced through Feed.maxStoredPosts (see privateFeedMaxStoredPosts), the age limits through
    // Feed.maxPostAgeDays (see privateFeedMaxAgeDays) and the sync server's archive-expiry job; the
    // storage limits are not enforced yet — currently only surfaced to users in the UI.
    maxInboxAgeDays?: number;
    maxInboxMessages: number;      // per inbox
    maxInboxStorageMb: number;     // across all inboxes
    maxPrivateFeedAgeDays?: number;
    maxPrivateFeedMessages: number; // per feed
    maxPrivateFeedStorageMb: number; // across all private feeds
    // Private storage (newsletter inboxes and private feeds) is deleted after this many
    // days without any activity on the account; undefined = kept indefinitely. The account
    // and its subscriptions stay. Not enforced yet — currently only surfaced in the UI.
    maxInactivityDays?: number;
}

// Anonymous users have no plan and cannot store private content at all.
export const anonStorageLimits = {
    maxInboxAgeDays: 0,
    maxInboxMessages: 0,
    maxInboxStorageMb: 0,
    maxPrivateFeedAgeDays: 0,
    maxPrivateFeedMessages: 0,
    maxPrivateFeedStorageMb: 0,
} as const;

const planLimits: Record<UserPlan, PlanLimits> = {
    free: { maxFeeds: 200, maxPrivateFeeds: 5, maxNewsletterFeeds: 5, maxCreatedFeeds: 500, keywordFilters: false,
        maxInboxAgeDays: 45, maxInboxMessages: 100, maxInboxStorageMb: 100,
        maxPrivateFeedAgeDays: 180, maxPrivateFeedMessages: 1000, maxPrivateFeedStorageMb: 100,
        maxInactivityDays: 90 },
    pro: { maxFeeds: 1000, maxPrivateFeeds: 100, maxNewsletterFeeds: 100, maxCreatedFeeds: 500, keywordFilters: true,
        maxInboxMessages: 1000, maxInboxStorageMb: 1000,
        maxPrivateFeedMessages: 1000, maxPrivateFeedStorageMb: 1000 },
    vip: { maxFeeds: 1000, maxPrivateFeeds: 100, maxNewsletterFeeds: 100, maxCreatedFeeds: 500, keywordFilters: true,
        maxInboxMessages: 1000, maxInboxStorageMb: 1000,
        maxPrivateFeedMessages: 1000, maxPrivateFeedStorageMb: 1000 },
    // Same as 'pro' except for a much smaller storage cap and the inactivity limit.
    earlyAdopter: { maxFeeds: 1000, maxPrivateFeeds: 100, maxNewsletterFeeds: 100, maxCreatedFeeds: 500, keywordFilters: true,
        maxInboxMessages: 1000, maxInboxStorageMb: 100,
        maxPrivateFeedMessages: 1000, maxPrivateFeedStorageMb: 100,
        maxInactivityDays: 90 },
    // Intentionally tiny so quota limits are easy to hit while testing. Admin-assign only.
    test: { maxFeeds: 6, maxPrivateFeeds: 2, maxNewsletterFeeds: 2, maxCreatedFeeds: 3, keywordFilters: true,
        maxInboxAgeDays: 2, maxInboxMessages: 5, maxInboxStorageMb: 1,
        maxPrivateFeedAgeDays: 2, maxPrivateFeedMessages: 5, maxPrivateFeedStorageMb: 1 },
};

export function getPlanLimits(plan: UserPlan): PlanLimits {
    return planLimits[plan];
}

/**
 * The post-storage cap (Feed.maxStoredPosts) a plan allows for one private-like feed: the
 * per-inbox message limit for NEWSLETTER feeds, the per-feed limit for PRIVATE feeds. Set at feed
 * creation and re-applied to all of a user's private-like feeds when their plan changes. Used as
 * the cap verbatim: both purge paths (the writer's boundary-archive deletion and the nightly
 * archive purge) round towards KEEPING, so the physical store holds at least this many posts and
 * at most POSTS_PER_FILE-1 more — no headroom needs to be added here.
 */
export function privateFeedMaxStoredPosts(plan: UserPlan, feedStatus: 'PRIVATE' | 'NEWSLETTER'): number {
    const limits = getPlanLimits(plan);
    return feedStatus == 'NEWSLETTER' ? limits.maxInboxMessages : limits.maxPrivateFeedMessages;
}

/**
 * The storage allowance in MB (Feed.maxStorageMb) a plan brings to a private-like feed: the
 * combined inbox storage for NEWSLETTER feeds, the combined private-feed storage for PRIVATE feeds.
 * Both limits cover all of a user's feeds of that type, not the one feed.
 */
export function privateFeedMaxStorageMb(plan: UserPlan, feedStatus: 'PRIVATE' | 'NEWSLETTER'): number {
    const limits = getPlanLimits(plan);
    return feedStatus == 'NEWSLETTER' ? limits.maxInboxStorageMb : limits.maxPrivateFeedStorageMb;
}

/**
 * The age limit (Feed.maxPostAgeDays) a plan brings to one private-like feed: the inbox age limit
 * for NEWSLETTER feeds, the private-feed one for PRIVATE feeds. 0 = the plan has no age limit.
 * Enforced archive-by-archive by the sync server's archive-expiry job.
 */
export function privateFeedMaxAgeDays(plan: UserPlan, feedStatus: 'PRIVATE' | 'NEWSLETTER'): number {
    const limits = getPlanLimits(plan);
    return (feedStatus == 'NEWSLETTER' ? limits.maxInboxAgeDays : limits.maxPrivateFeedAgeDays) ?? 0;
}
